import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DateService {

  private timeZone = new BehaviorSubject('');
  timeZoneValue = this.timeZone.asObservable().pipe(distinctUntilChanged(), shareReplay(1));

  changeTimeZoneValue(zone: string) {
    const normalized = zone?.trim();
    if (normalized && normalized !== this.timeZone.value) {
      this.timeZone.next(normalized);
    }
  }

  getCurrentTimeZone(): string {
    return this.timeZone.value;
  }

  monthArray = [{ monthName: 'January', monthNum: '01', monthAbbrev: 'jan' },
  { monthName: 'February', monthNum: '02', monthAbbrev: 'feb' },
  { monthName: 'March', monthNum: '03', monthAbbrev: 'mar' },
  { monthName: 'April', monthNum: '04', monthAbbrev: 'apr' },
  { monthName: 'May', monthNum: '05', monthAbbrev: 'may' },
  { monthName: 'June', monthNum: '06', monthAbbrev: 'jun' },
  { monthName: 'July', monthNum: '07', monthAbbrev: 'jul' },
  { monthName: 'August', monthNum: '08', monthAbbrev: 'aug' },
  { monthName: 'September', monthNum: '09', monthAbbrev: 'sep' },
  { monthName: 'October', monthNum: '10', monthAbbrev: 'oct' },
  { monthName: 'November', monthNum: '11', monthAbbrev: 'nov' },
  { monthName: 'December', monthNum: '12', monthAbbrev: 'dec' },
  ];
  datePipe: DatePipe = new DatePipe('en-US');

  constructor() { }

  utcDate(dateValueLocal: string): string
  {
    // returns 'yyyy-MM-dd'
    if (!dateValueLocal) return null;

    const dateParts: string[] = dateValueLocal.split("-");

    const yearValue: string  = dateParts[0];
    const monthValue: string = ('0' + (dateParts[1])).slice(-2);
    const dateValue: string  = ('0' + dateParts[2]).slice(-2);

    return yearValue + '-' + monthValue + '-' + dateValue;
  }

  utcDateString(dt: Date): string
  {
    // returns 'yyyy-MM-dd'
    if (!dt) return null;

    const dateValueLocal = new Date(dt);
    const yearValue = dateValueLocal.getFullYear();
    const monthValue = ('0' + (dateValueLocal.getMonth() + 1)).slice(-2);
    const dateValue  = ('0' + dateValueLocal.getDate()).slice(-2);

    return yearValue + '-' + monthValue + '-' + dateValue;
  }

  utcDateMMDDYYYY(dateValueLocal: string): string
  {
    // returns 'MM-dd-yyyy'
    if (!dateValueLocal) return null;

    const dateParts: string[] = dateValueLocal.split("-");

    const yearValue: string  = dateParts[0];
    const monthValue: string = ('0' + (dateParts[1])).slice(-2);
    const dateValue: string  = ('0' + dateParts[2]).slice(-2);

    return monthValue + '-' + dateValue + '-' + yearValue;
  }

  utcDateMMDDYYYYString(dt: Date): string
  {
    // returns 'MM-dd-yyyy'

    if (!dt) return null;

    const dateValueLocal: Date = new Date(dt);

    const yearValue: number  = dateValueLocal.getFullYear();
    const monthValue: string = ('0' + (dateValueLocal.getMonth() + 1)).slice(-2);
    const dateValue : string = ('0' + dateValueLocal.getDate()).slice(-2);

    return monthValue + '-' + dateValue + '-' + yearValue;
  }

  utcDateMMDDYYYYHHMMSS(dateValueLocal): string
  {
    // returns 'yyMMdd-hhmmss'

    if (!dateValueLocal) return null;

    const yearValue: string = ('' + dateValueLocal.getUTCFullYear()).slice(-2);
    const monthValue: string = ('0' + (dateValueLocal.getUTCMonth() + 1)).slice(-2);
    const dateValue: string = ('0' + dateValueLocal.getUTCDate()).slice(-2);
    const hoursValue: string = ('0' + dateValueLocal.getUTCHours()).slice(-2);
    const minutesValue: string = ('0' + dateValueLocal.getUTCMinutes()).slice(-2);
    const secondsValue: string = ('0' + dateValueLocal.getUTCSeconds()).slice(-2);

    return yearValue + monthValue + dateValue + '-' + hoursValue + minutesValue + secondsValue;
  }

  utcDatePrintValue(dateValueLocal) {
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    if (dateValueLocal == null) {
      return null;
    }
    const yearValue = dateValueLocal[0];
    const monthValue = ('0' + (dateValueLocal[1])).slice(-2);
    const dateValue = ('0' + dateValueLocal[2]).slice(-2);
    return monthNames[Number(monthValue) - 1] + ' ' + dateValue + ', ' + yearValue;
  }

  public getTodayMonthFilter() {
    const date = new Date();
    const month = date.getMonth();
    return {
      date: [''],
      filter: ['MONTH'],
      singleDate: [''],
      months: [this.monthArray[month].monthAbbrev],
      years: [date.getUTCFullYear()],
      quarter: ['Q1'],
      startDate: [''],
      endDate: ['']
    }
  }

  public getTodayDateFilter() {
    const date = new Date();
    const month = date.getMonth();
    return {
      date: [''],
      filter: ['DATE'],
      singleDate: new Date(),
      months: [''],
      years: [''],
      quarter: [''],
      startDate: [''],
      endDate: ['']
    }
  }

  public getTodayQuarterFilter() {
    const date = new Date();
    return {
      date: [''],
      filter: ['QUARTER'],
      singleDate: [''],
      months: [''],
      years: [date.getUTCFullYear()],
      quarter: ['Q1'],
      startDate: [''],
      endDate: ['']
    }
  }

  getCommunityTime() {
    if (this.timeZone.value) {
      let currentCommunityTime = this.getTimeBaseOnTimeZoneName(this.timeZone.value);
      return this.timeToMinutes(currentCommunityTime.split(':')[0], currentCommunityTime.split(':')[1]);
    } else {
      return null;
    }
  }

  getTimeBaseOnTimeZoneName(timeZone) {
    let date = new Date();
    let hrs = date.toLocaleString('en-US', { hour: '2-digit', hour12: false, timeZone: timeZone ? timeZone : "" })
    let mins = date.toLocaleString('en-US', { minute: '2-digit', hour12: false, timeZone: timeZone ? timeZone : "" })
    return hrs + ':' + mins;
  }

  remainingTimeIsLessThanConfigured(adlLastTime: any) {
    if (this.timeZone.value) {
      let currentCommunityTime = this.getTimeBaseOnTimeZoneName(this.timeZone.value);
      let CurrentCommunityTimeMinutes = this.timeToMinutes(currentCommunityTime.split(':')[0], currentCommunityTime.split(':')[1]);
      let adlLastTimeMinutes = this.timeToMinutes(adlLastTime, 0);
      let minutesRemaining;
      if (adlLastTimeMinutes > CurrentCommunityTimeMinutes) {
        minutesRemaining = adlLastTimeMinutes - CurrentCommunityTimeMinutes;
      } else {
        return false;
      }
      if (adlLastTime && minutesRemaining < environment.highLightCareTimeMins) {
        return true;
      }
      return false;
    }
  }

  timeToMinutes(hours, minutes) {
    return Number(hours * 60) + Number(minutes);
  }

  /**
   * Date filter form related methods
   */
  getStartDateForFilter(filterForm) {
    return this.getDatesForFilter(filterForm).startDate;
  }
  getEndDateForFilter(filterForm) {
    return this.getDatesForFilter(filterForm).endDate;
  }

  getDatesForFilter(data) {
    if (data && data.filter == 'MONTH') {
      this.getNextMonths(data.months, data.years, 1);
    }
    if (data && data.filter == 'QUARTER') {
      this.getQuarterDetails(data.quarter, data.years);
    }
    if (data && data.filter == 'YTD') {
      this.yearIndexValue = Number((new Date()).getFullYear());
      const firstDay = new Date(this.yearIndexValue, 1, 1);
      this.startDate = this.datePipe.transform(firstDay, 'yyyy-MM-dd');
      let lastDay = new Date();
      this.endDate = this.datePipe.transform(lastDay, 'yyyy-MM-dd');
    }
    if (data != undefined && data.filter == 'CUSTOM') {
      this.startDate = null;
      this.endDate = null;
      if (data.startDate && data.startDate[0] == undefined)
        this.startDate = this.datePipe.transform(data.startDate, 'yyyy-MM-dd');
      if (data.endDate && data.endDate[0] == undefined)
        this.endDate = this.datePipe.transform(data.endDate, 'yyyy-MM-dd');
    }
    if (data != undefined && data.filter == 'DATE') {
      this.startDate = this.datePipe.transform(data.singleDate instanceof Date ? data.singleDate : new Date(), 'yyyy-MM-dd');
    }
    return { startDate: this.startDate, endDate: this.endDate }
  }

  getQuarterDetails(quarterValue: string, yearValue: string) {
    if (quarterValue == 'Q1') {
      this.getNextMonths('jan', yearValue, 3);
    }
    if (quarterValue == 'Q2') {
      this.getNextMonths('apr', yearValue, 3);
    }
    if (quarterValue == 'Q3') {
      this.getNextMonths('jul', yearValue, 3);
    }
    if (quarterValue == 'Q4') {
      this.getNextMonths('oct', yearValue, 3);
    }
  }

  getNextMonths(monthValue: string, yearValue: string, monthsQuantity: number) {
    this.monthIndexValue = this.monthIndex[monthValue];
    this.yearIndexValue = Number(yearValue);
    const firstDay = new Date(this.yearIndexValue, this.monthIndexValue, 1);
    this.startDate = this.datePipe.transform(firstDay, 'yyyy-MM-dd');
    const lastDay = new Date(this.yearIndexValue, this.monthIndexValue + monthsQuantity, 0);
    this.endDate = this.datePipe.transform(lastDay, 'yyyy-MM-dd');
    this.maxMonthDate = Number(this.endDate.split('-')[2]);
  }

  monthIndexValue = 0;
  yearIndexValue = 0;
  startDate = '';
  endDate = '';
  eventData = [];
  maxMonthDate = 0;

  monthIndex = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11
  };

  getZonedDateTime(timeZone) {
    const now = new Date();

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(now);
    const get = type => parts.find(p => p.type === type)?.value;

    const year = get('year');
    const month = get('month');
    const day = get('day');
    const hour = get('hour');
    const minute = get('minute');
    const second = get('second');

    return {
      isoTime: `${hour}:${minute}:${second}`,
      isoDateTime: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
      isoDate: `${year}-${month}-${day}`,
      year: `${year}`,
      month: `${month}`,
      day: `${day}`,
      hour: `${hour}`,
      minute: `${minute}`,
      second: `${second}`
    };
  }

  currentCommunityTime(): string {
    return this.getZonedDateTime(this.timeZone.value).isoTime;
  }

  currentCommunityDate(): string {
    return this.getZonedDateTime(this.timeZone.value).isoDate;
  }

  currentCommunityDateTime(): string {
    return this.getZonedDateTime(this.timeZone.value).isoDateTime;
  }

  currentCommunityTimeParts() {
    return this.getZonedDateTime(this.timeZone.value);
  }
}
